import { Injectable, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { combineLatest, filter, map, Observable } from "rxjs";

import { BreakpointService } from "@hermes/utils-generic-services-user-interface";

import {
    hideLoader,
    hideNotification,
    keyboardNavigation,
    mouseNavigation,
    setHeaderTransparency,
    setCanHeaderBeTransparent,
    showLoader,
    showNotification,
    updateHeaderProperties as updateHeaderProperties,
    setCookiePopinVisible,
    setLoaderType,
    setBlackOverlayVisible,
} from "../actions/layout.actions";
import { IHeaderMainContainerProperties } from "../model/header-properties.model";
import { LoaderType } from "../model/loader.model";
import { MessageOption } from "../model/notification.model";
import { State } from "../reducers/layout.reducer";
import {
    selectCookiePopinVisible,
    selectDisplayOverlay,
    selectHeader,
    selectHeaderAndBannerHeight,
    selectHeaderHeight,
    selectHeaderHeightWithLinks,
    selectHeaderTransparency,
    selectCanHeaderBeTransparent,
    selectKeyboardNavigation,
    selectLoaderState,
    selectLoaderType,
    selectMenubarHeight,
    selectNotificationMessage,
    selectIsFixedMenu,
    selectBlackOverlayVisible,
} from "../selectors/layout.selectors";

@Injectable()
export class LayoutFacade {
    private store = inject<Store<State>>(Store);
    private breakpointService = inject(BreakpointService);

    public headerMainHeight$ = this.store.select(selectHeaderHeight);
    public headerHeightWithLinks$ = this.store.select(
        selectHeaderHeightWithLinks,
    );
    public headerAndBannerHeight$ = this.store.select(
        selectHeaderAndBannerHeight,
    );
    public menubarHeight$ = this.store.select(selectMenubarHeight);
    public headerProperties$ = this.store.select(selectHeader);
    public headerTransparent$ = this.store.select(selectHeaderTransparency);
    public canHeaderBeTransparent$ = this.store.select(
        selectCanHeaderBeTransparent,
    );
    public isFixedMenu$ = this.store.select(selectIsFixedMenu);
    public notificationMessage$ = this.store.select(selectNotificationMessage);
    public displayOverlay$ = this.store.select(selectDisplayOverlay);
    public blackOverlayVisible$ = this.store.select(selectBlackOverlayVisible);
    public keyboardNavigation$ = this.store.select(selectKeyboardNavigation);
    public selectLoaderState$ = this.store.select(selectLoaderState);
    public selectLoaderType$ = this.store.select(selectLoaderType);
    public isCookiePopinVisible$ = this.store.select(selectCookiePopinVisible);

    private originalFullHeaderHeight: number = 0;

    public selectLoaderState = toSignal(this.selectLoaderState$, {
        requireSync: true,
    });

    public selectLoaderType = toSignal(this.selectLoaderType$, {
        requireSync: true,
    });

    /**
     * Calculates the header height after the user scrolls down.
     * The height depends on whether the horizontal menu is present in the header.
     *
     * For viewports smaller than 1024px:
     * - The entire main header height is used regardless of other conditions.
     *
     * For viewports 1024px or larger:
     * - Upon scrolling down, the main header retracts and progressively disappears.
     * - Only the menubar remains fixed at the top of the page.
     * - Therefore, we need only the height of the menubar for accurate scroll calculations.
     */
    public headerHeightAfterScrollingDown$: Observable<number> = combineLatest([
        this.headerMainHeight$,
        this.menubarHeight$,
    ]).pipe(
        map(([headerMainHeight, menubarHeight]) => {
            if (this.isMenuHorizontalBreakpointMatched) {
                return headerMainHeight;
            }
            return menubarHeight;
        }),
        filter((height) => height !== undefined),
    );

    private get isMenuHorizontalBreakpointMatched(): boolean {
        return !this.breakpointService.mediumBreakpointMatcher();
    }

    public getOriginalFullHeaderHeight(): number {
        return this.originalFullHeaderHeight;
    }

    public setOriginalFullHeaderHeight(height: number): void {
        if (!this.originalFullHeaderHeight) {
            this.originalFullHeaderHeight = height;
        }
    }

    public updateHeaderProperties(
        header: Partial<IHeaderMainContainerProperties>,
    ): void {
        this.store.dispatch(updateHeaderProperties({ header }));
    }

    public setHeaderTransparency(isTransparent: boolean): void {
        this.store.dispatch(setHeaderTransparency({ isTransparent }));
    }

    public setCanHeaderBeTransparent(canHeaderBeTransparent: boolean): void {
        this.store.dispatch(
            setCanHeaderBeTransparent({ canHeaderBeTransparent }),
        );
    }

    public showLoader(loaderType = LoaderType.LOADER_TYPE_IMAGE): void {
        this.store.dispatch(showLoader());
        // Enforce default loader (type should be remove and loader replaced by a unique loader)
        this.store.dispatch(setLoaderType({ loaderType }));
    }

    public hideLoader(): void {
        this.store.dispatch(hideLoader());
    }

    public setLoaderType(loaderType: LoaderType) {
        this.store.dispatch(setLoaderType({ loaderType }));
    }

    public showNotification(
        displayOverlay: boolean,
        messageOption: MessageOption,
    ): void {
        this.store.dispatch(
            showNotification({
                displayOverlay,
                messageOption,
            }),
        );
    }

    public hideNotification(): void {
        this.store.dispatch(hideNotification());
    }

    public enableKeyboardNavigation(): void {
        this.store.dispatch(keyboardNavigation());
    }

    public enableMouseNavigation(): void {
        this.store.dispatch(mouseNavigation());
    }

    public setCookiePopinVisible(isVisible: boolean): void {
        this.store.dispatch(setCookiePopinVisible({ isVisible }));
    }

    public setBlackOverlayVisible(isVisible: boolean): void {
        this.store.dispatch(setBlackOverlayVisible({ isVisible }));
    }
}
